import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaMap, FaFilter } from 'react-icons/fa';
import { TbLayoutListFilled } from 'react-icons/tb';
import { LuPanelLeftOpen } from 'react-icons/lu';
import { FilterDropdown } from './FilterDropdown';
import { CreateDropdown } from './CreateDropdown';
import { NotificationButton } from './NotificationButton';
import { UserInfo } from './UserInfo';
import { NewProjectForm } from './NewProjectForm';
import { GlobalSearch } from "./GlobalSearch";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { RISK_LEVELS, type RiskLevel } from "../constants/project";

// Helper function to generate risk level labels
const getRiskLevelLabel = (level: RiskLevel): string => {
  switch (level) {
    case RISK_LEVELS.NONE:
      return 'No Risk';
    case RISK_LEVELS.LOW:
      return 'Low Risk';
    case RISK_LEVELS.MEDIUM:
      return 'Medium Risk';
    case RISK_LEVELS.HIGH:
      return 'High Risk';
    case RISK_LEVELS.CRITICAL:
      return 'Critical Risk';
    default:
      return `${level} Risk`;
  }
};

const VIEWS = [
  {
    value: 'map',
    label: 'Map view'
  },
  {
    value: 'projects',
    label: 'List view'
  },
];

const SORTING = [
  {
    value: 'name',
    label: 'Name (A-Z)'
  },
  {
    value: 'name_desc',
    label: 'Name (Z-A)'
  },
  {
    value: 'status',
    label: 'Status'
  },
  {
    value: 'created_date',
    label: 'Created Date (Newest)'
  },
  {
    value: 'created_date_desc',
    label: 'Created Date (Oldest)'
  },
  {
    value: 'updated_date',
    label: 'Updated Date (Newest)'
  },
  {
    value: 'updated_date_desc',
    label: 'Updated Date (Oldest)'
  },
  {
    value: 'risk',
    label: 'Risk (Low to High)'
  },
  {
    value: 'risk_desc',
    label: 'Risk (High to Low)'
  },
];



interface DashboardFiltersProps {
  view: 'map' | 'projects';
  showMenu?: boolean;
  setShowMenu?: (value: boolean) => void;
  onSortChange?: (value: string) => void;
  onPriorityFilter?: (value: string) => void;
  onViewChange?: (value: 'map' | 'projects') => void;
  selectedRiskLevel?: string;
}

export const DashboardFilters = ({ view, showMenu, setShowMenu = () => {}, onSortChange, onPriorityFilter, onViewChange, selectedRiskLevel = 'all' }: DashboardFiltersProps) => {
  const navigate = useNavigate();
  const [showNewProject, setShowNewProject] = useState<boolean>(false);
  const { isFeatureEnabled } = useFeatureFlag();

  const currentView = VIEWS.find(v => v.value === view) || VIEWS[0];

  const PRIORITY_FILTER = [
    {
      value: 'all',
      label: 'All Risk'
    },
    ...Object.values(RISK_LEVELS).map((level: RiskLevel) => ({
      value: level.toLowerCase(),
      label: getRiskLevelLabel(level)
    }))
  ];

  return (
    <>
      {showNewProject && (
        <NewProjectForm 
          onClose={() => setShowNewProject(false)} 
          onProjectCreated={async (projectId: string) => {
            // Set active tab to team for the newly created project
            localStorage.setItem('activeProjectTab', 'team');
            // Navigate to the new project's Team tab
            navigate(`/projects/${projectId}`);
            setShowNewProject(false);
          }}
        />
      )}
      <div className="dashboard-filters absolute flex gap-2 px-4 mt-3 z-[100]" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
        {!showMenu && <button onClick={() => setShowMenu(true)} className="text-[#888] hover:text-[#444] hover:bg-[#fafafa] rounded-lg p-1.5 transition-all duration-150 border border-[#eaeaea] bg-white h-9 w-9 flex items-center justify-center"><LuPanelLeftOpen className="w-4 h-4" /></button>}
        <div className="flex justify-between w-full">
          <div className="flex items-center gap-2.5">
            {isFeatureEnabled('GLOBAL_SEARCH') && (
              <div className="w-72">
                <GlobalSearch />
              </div>
            )}
            <FilterDropdown
              options={VIEWS}
              defaultValue={view}
              defaultLabel={currentView.label}
              icon={view === 'map' ? <FaMap /> : <TbLayoutListFilled />}
              onChange={onViewChange ? (value: string) => onViewChange(value as 'map' | 'projects') : ((value) => {
                if (value === 'map') {
                  navigate('/dashboard'); // Map view leads to dashboard
                } else if (value === 'projects') {
                  navigate('/projects'); // List view leads to projects
                }
              })}
            />
            {isFeatureEnabled('RISK_LEVEL_FILTER') && (
              <FilterDropdown
                options={PRIORITY_FILTER}
                defaultValue={selectedRiskLevel}
                defaultLabel="All Risk"
                icon={<FaFilter />}
                onChange={onPriorityFilter}
              />
            )}
            {view === 'projects' && (
              <FilterDropdown
                options={SORTING}
                defaultLabel="Sorting"
                onChange={onSortChange}
              />
            )}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <CreateDropdown onSelectProject={() => setShowNewProject(true)} />
            <NotificationButton />
            <UserInfo />
          </div>
        </div>
      </div>
    </>
  );
};
